import * as yup from "yup";

import type { TFunction } from "i18next";

/**
 * `projectId`, `activityId` y `assignedProjectId` salen como string: los
 * `<Select>` de MUI entregan el id numérico del `<MenuItem>` y `yup.string()` lo
 * castea al validar, aunque `CreateTimeEntryDTO` los declare numéricos.
 * Comportamiento preexistente (hallazgo previo a la migración); no se corrige
 * en esta migración tipo-only.
 */
export interface ChargedHourValues {
  projectId: string;
  activityId: string;
  spentOn: Date;
  hours: number;
  comment: string;
  /** `Maybe<string>` de yup incluye `null` además de `undefined`. */
  assignedProjectId?: string | null;
}

export interface BuildChargedHourSchemaOptions {
  /** Retorna true si la actividad elegida es festiva o licencia sin goce: en
   * ese caso el mínimo de horas baja a 0. */
  isFestive: () => boolean;
  /** Si el usuario tiene el permiso "Estructura" o "Estructura Operativa",
   * assignedProjectId es requerido. Se pasa como booleano de construcción
   * porque yup.when() requiere un campo hermano existente en el schema, y
   * hasAssignedProjectPermission no existe como campo del formulario; usar
   * when() sobre él nunca dispararía la validación. */
  hasAssignedProjectPermission: boolean;
  t: TFunction;
}

/**
 * Construye el schema de validación para la imputación general de horas.
 */
export const buildChargedHourSchema = ({
  isFestive,
  hasAssignedProjectPermission,
  t,
}: BuildChargedHourSchemaOptions): yup.ObjectSchema<ChargedHourValues> => {
  const doNotRequireHours = isFestive();
  const minHours = doNotRequireHours ? 0 : 1;
  const hoursMessage = t("minimumValueShouldBeHour", { minHours });

  return yup.object().shape({
    projectId: yup.string().typeError(t("isRequired")).required(t("isRequired")),
    activityId: yup.string().typeError(t("isRequired")).required(t("isRequired")),
    spentOn: yup.date().typeError(t("isRequired")).required(t("isRequired")),
    hours: yup
      .number()
      .typeError(t("isRequired"))
      .required(t("isRequired"))
      .min(minHours, hoursMessage),
    comment: yup.string().typeError(t("isRequired")).required(t("isRequired")),
    assignedProjectId: hasAssignedProjectPermission
      ? yup.string().required(t("isRequired"))
      : yup.string().notRequired(),
  }).required();
};
